import { Dispatch, SetStateAction, useMemo, useState } from 'react'
import { Sidebar } from 'primereact/sidebar'
import { Button } from 'primereact/button'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

// local imports
import { IFilteredDoctorList, IGetDoctorTimeTableList } from 'types/HealthType'
import DateUtil from 'utils/DateUtil'
import { useSaveDoctorAppointment } from '../hooks'

/* assets */
import calendarr from 'assets/icons/calendar-gray.svg'
import time from 'assets/icons/time.svg'
import location from 'assets/icons/location-marker-gray.svg'
import building from 'assets/icons/bina-gray.svg'
import health from 'assets/icons/health-logo.svg'
import branch from 'assets/icons/list-branch.svg'
import resIcon from 'assets/icons/doctor-res.svg'
import FavoriteIconButton from 'components/ui/FavoriteIconButton'
import HealthService from 'service/HealthService'
import AuthUtil from 'utils/AuthUtil'

interface ScheduleSidebarProps {
  openModal: () => void
  closeAfterSave: () => void
  visible: boolean
  setVisible: Dispatch<SetStateAction<boolean>>
  docInfo: IFilteredDoctorList
  appointmentInfo: IGetDoctorTimeTableList
}

export default function ScheduleSidebar({
  docInfo,
  appointmentInfo,
  visible,
  setVisible,
  openModal,
  closeAfterSave,
}: ScheduleSidebarProps) {
  const { t } = useTranslation()
  const date = DateUtil.dateFormat(new Date(appointmentInfo.reservationEndHour))
  const appointmentTime = `${appointmentInfo.reservationStartHourStr} - ${appointmentInfo.reservationEndHourStr}`
  const employee = AuthUtil.getEmployeeSummary()

  // Doctor information desctructuring
  const { block, floor, location, specialty, healthServiceDoctorId, isFavorite } = docInfo || {}
  const docFullName = `${docInfo.title} ${docInfo.name} ${docInfo.surname}`

  /* Handle Save Doctor Appointment | Custom Hook */
  const { loading, saveDoctorAppointment } = useSaveDoctorAppointment(appointmentInfo, closeAfterSave)

  const closeSidebar = () => {
    setVisible(false)
    openModal()
  }

  const appointmentInfoItems = useMemo(
    () => [
      {
        id: 'date-info',
        icon: calendarr,
        title: t('appointment-modal.appointment-date'),
        value: date,
      },
      {
        id: 'time-info',
        icon: time,
        title: t('appointment-modal.appointment-time'),
        value: appointmentTime,
      },
      {
        id: 'location-info',
        icon: location,
        title: t('appointment-modal.doctor-location'),
        value: location,
      },
      {
        id: 'block-floor-info',
        icon: building,
        title: t('appointment-modal.block-floor'),
        value: `${block}  -  ${floor}`,
      },
      {
        id: 'doc-name',
        icon: health,
        title: t('appointment-modal.doctor-name'),
        value: docFullName,
      },
      {
        id: 'doc-specialty',
        icon: branch,
        title: t('appointment-modal.doctor-specialty'),
        value: specialty,
      },
    ],
    [block, floor, location, specialty, appointmentTime, date, docFullName, t]
  )

  /* Favorite Doctor */
  const [favorite, setFavorite] = useState(isFavorite)
  const handleAddFavorite = () => {
    HealthService.addDoctorFavorite(healthServiceDoctorId).then((r) => setFavorite(true))
  }
  const handleRemoveFavorite = () => {
    HealthService.removeDoctorFavorite(healthServiceDoctorId).then((r) => setFavorite(false))
  }

  return (
    <Sidebar
      style={{ width: '500px' }}
      className="of-bordered-sidebar-box"
      visible={visible}
      position="right"
      onHide={closeSidebar}
      dismissable={false}
      closeOnEscape={false}>
      <SidebarContainer>
        <ContentArea>
          <Header className="d-flex align-items-center gap-2">
            {t('appointment-modal.doctor-appointment')}
            {AuthUtil.isAdmin(employee?.authList) && (
              <FavoriteIconButton onSave={handleAddFavorite} onRemove={handleRemoveFavorite} isFavorite={favorite} />
            )}
          </Header>
          <AppointmentSummary>{t('appointment-modal.appointment-summary')}</AppointmentSummary>
          <ImagePreview url={resIcon} />
          <InfoList>
            <div className="appointment-subtitle">{t('appointment-modal.appointment-info')}</div>
            {appointmentInfoItems.map((info) => (
              <InfoListItem key={info.id}>
                <img src={info.icon} alt={info.id} />
                <span className="appointment-info-title"> {info.title} : </span>
                <span className="appointment-info-value">{info.value}</span>
              </InfoListItem>
            ))}
          </InfoList>
        </ContentArea>

        {/* Action Buttons */}
        <ActionArea>
          <CancelButton
            id="backToDoctorAppointment"
            className="p-button-outlined"
            label={t('appointment-modal.back-to-modal')}
            onClick={closeSidebar}
          />

          <ConfirmButton
            id="saveDoctorAppointment"
            loading={loading}
            label={t('buttons.appointment')}
            onClick={saveDoctorAppointment}
          />
        </ActionArea>
      </SidebarContainer>
    </Sidebar>
  )
}

const SidebarContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const ContentArea = styled.div`
  flex-grow: 1;
`

const ActionArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  padding: 1rem 0 0.5rem;
`

const Header = styled.div`
  font-weight: 700;
  font-size: 20px;
  line-height: 24px;
  margin-bottom: 1rem;
`
const AppointmentSummary = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  color: #222e47;
`
const ImagePreview = styled.div`
  background: ${(props: { url: string }) => `url(${props.url})`};
  width: 150px;
  height: 150px;
  margin: 10px 0;
`

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;

  .appointment-subtitle {
    font-size: 1rem !important;
    font-weight: 700;
    line-height: 19px;
  }
`

const InfoListItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  font-size: 14px;
  line-height: 17px;

  img {
    width: 20px;
    height: 20px;
  }

  .appointment-info-title {
    font-weight: 700;
    white-space: nowrap;
  }

  .appointment-info-value {
    word-break: break-words;
    word-wrap: break-word;
  }
`

const CancelButton = styled(Button)`
  width: 100%;
  height: 50px;
  background: #45bfe5;
  border-radius: 10px;
`
const ConfirmButton = styled(Button)`
  width: 100%;
  height: 50px;
  background: #45bfe5;
  border-radius: 10px;
  justify-content: center;
`
